import {
  Box,
  Button,
  Drawer,
  DrawerProps,
  Fab,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableCellProps,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import React, { useContext } from 'react';
import { Formik, FormikHelpers, useFormikContext } from 'formik';
import { Container } from '../../types';
import {
  ClientSelectAsync,
  ContainerTypeSelectAsync,
  ShippingLineSelectAsync,
  SubmitButton,
  ThemedDatePicker,
  ThemedRadioCheck,
  ThemedTextField,
  ThemedTimePicker,
} from './Form';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import set from 'lodash/set';
import cloneDeep from 'lodash/cloneDeep';
import useContainerApiRoutes from '../hooks/api/useContainerApiRoutes';
import SnackBarContext from '../context/snackbar';
import { ConfirmationServiceContext } from '../context/modal';
import { ImageUpload } from './FileUploads';

type Props = DrawerProps & {
  open: boolean;
  onClose: (event: {}, reason: 'backdropClick' | 'escapeKeyDown' | 'closeButtonClick' | 'containersAdded') => void;
};

// My Stack and Virtual Stack containers are added through two separate tables,
// both always visible, so a mixed batch can be submitted in one go.
export interface MyFormValues {
  myStackContainers: Partial<Container>[];
  virtualStackContainers: Partial<Container>[];
}

// No pickup site for My Stack: reuse is internal, the backend assigns the
// adding user's own client record.
const initMyStackContainer = (): Partial<Container> => ({
  myStack: true,
  shippingLineCode: '',
  containerTypeCode: '',
  containerCode: '',
  shippingLineBookingRef: '',
});

const initVirtualStackContainer = (): Partial<Container> => ({
  myStack: false,
  shippingLineCode: '',
  containerTypeCode: '',
  containerCode: '',
  availableFrom: '',
  availableTimeFrom: '',
  availableTo: '',
  collectionTime: '',
  client_id: '',
  cleanAndAcessible: false,
  photos: [],
  inStack: true,
});

const AddContainerDrawer = (props: Props) => {
  const { open, onClose } = props;
  const classes = useStyles();
  const { createContainers } = useContainerApiRoutes();
  const { showSnack } = useContext(SnackBarContext);
  const { showConfirmationModal, setOpenId } = useContext(ConfirmationServiceContext);

  const validate = (values: MyFormValues) => {
    const errors: Partial<MyFormValues | undefined> = {};
    values.myStackContainers.forEach((container, index) => {
      // My Stack containers are reused by the adding user, so only the core
      // fields (plus the export booking for the reuse request) are required.
      const requiredFields: (keyof Container)[] = [
        'shippingLineCode',
        'containerTypeCode',
        'containerCode',
        'shippingLineBookingRef',
      ];
      requiredFields.forEach((requiredField) => {
        if (!container[requiredField]) {
          set(errors, `myStackContainers.${index}.${requiredField}`, 'Required');
        }
      });
    });
    values.virtualStackContainers.forEach((container, index) => {
      const requiredFields: (keyof Container)[] = [
        'shippingLineCode',
        'containerTypeCode',
        'containerCode',
        'availableFrom',
        'availableTo',
        'client_id',
      ];
      requiredFields.forEach((requiredField) => {
        if (!container[requiredField]) {
          set(errors, `virtualStackContainers.${index}.${requiredField}`, 'Required');
        }
      });
      // Every Virtual Stack container enters the public pool, so cleanliness
      // must be confirmed.
      if (!container.cleanAndAcessible) {
        set(errors, `virtualStackContainers.${index}.cleanAndAcessible`, '*Cannot Stack unless confirmed');
      }
    });
    return errors;
  };

  const save = (values: MyFormValues, { setSubmitting }: FormikHelpers<MyFormValues>) => {
    const containers = [...values.myStackContainers, ...values.virtualStackContainers];
    const finish = () => {
      showSnack('Containers Created!', 'success');
      onClose({}, 'containersAdded');
    };
    createContainers(containers)
      .then(async (result) => {
        if (!result) return setSubmitting(false);
        if (!result.duplicates) return finish();
        // A live request already exists for the same container + booking ref —
        // this cannot be booked again. A different booking ref is the
        // legitimate way to re-use the container another time.
        const lines = result.duplicates.map((d) => {
          if (d.status === 'submission') return `• ${d.containerCode} / booking ${d.bookingRef} is listed twice in this submission`;
          const state = d.status === 'pending' ? 'awaiting the shipping line' : 'already confirmed for re-use';
          const who = d.sameAccount ? 'your account' : d.requestedBy || 'another account';
          const when = d.requestedAt ? ` on ${new Date(d.requestedAt).toLocaleDateString()}` : '';
          return `• ${d.containerCode} / booking ${d.bookingRef} — requested by ${who}${when}, ${state}`;
        });
        await showConfirmationModal({
          title: 'This container is already booked under this booking number',
          contentText: `${lines.join('\n')}\n\nNothing was submitted. The same container can be re-used again under a different booking number — change the booking ref to proceed.`,
          confirmLabel: 'OK',
          hideCancel: true,
        });
        setOpenId(undefined);
        setSubmitting(false);
      })
      .catch(() => setSubmitting(false));
  };

  const initialValues: MyFormValues = { myStackContainers: [], virtualStackContainers: [] };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} classes={{ paper: classes.drawerPaper }}>
      <Box className={classes.margins}>
        <IconButton classes={{ root: classes.close }} size="medium" onClick={(e) => onClose({}, 'closeButtonClick')}>
          <CloseIcon fontSize="large" color="primary" />
        </IconButton>

        <Box mb={3} pl={2}>
          <Typography variant="h4" color="primary">
            Add Container/s
          </Typography>
        </Box>
        <Formik initialValues={initialValues} validate={validate} onSubmit={save}>
          {({ handleSubmit, isSubmitting, values, setFieldValue }) => {
            const totalContainers = values.myStackContainers.length + values.virtualStackContainers.length;
            const addMyStackRow = () =>
              setFieldValue('myStackContainers', [...values.myStackContainers, initMyStackContainer()]);
            const addVirtualStackRow = () =>
              setFieldValue('virtualStackContainers', [...values.virtualStackContainers, initVirtualStackContainer()]);

            return (
              <form onSubmit={handleSubmit} className={classes.form}>
                <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                  <MyStackSection isSubmitting={isSubmitting} onAddRow={addMyStackRow} />
                  <VirtualStackSection isSubmitting={isSubmitting} onAddRow={addVirtualStackRow} />
                </Box>
                <Box pl={2} pt={2}>
                  {/* SubmitButton spreads props over its own disabled logic, so
                      only pass disabled when forcing it off for an empty form. */}
                  <SubmitButton {...(totalContainers === 0 ? { disabled: true } : {})}>Submit</SubmitButton>
                </Box>
              </form>
            );
          }}
        </Formik>
        <Box mt={4} display="flex">
          <InfoIcon color="primary" />
          <Typography variant="subtitle2" fontWeight={400} fontStyle="italic" color="textSecondary" ml={1}>
            Fineprint about confirming containers are clean and adding photo evidence for any future disputes. Prompt to
            confirm user accepts the Terms & Conditions for submitting a container into the VirtualStack™
          </Typography>
        </Box>
      </Box>
    </Drawer>
  );
};

const SectionActions = ({
  isSubmitting,
  onAddRow,
  onReplicate,
  hasRows,
}: {
  isSubmitting: boolean;
  onAddRow: () => void;
  onReplicate: () => void;
  hasRows: boolean;
}) => (
  <Box pl={2} pb={2}>
    <Button
      disabled={isSubmitting || !hasRows}
      onClick={onReplicate}
      startIcon={<AddIcon />}
      variant="outlined"
      color="primary"
      sx={{ marginRight: 1 }}
    >
      Replicate
    </Button>
    <Button disabled={isSubmitting} onClick={onAddRow} startIcon={<AddIcon />} variant="outlined" color="secondary">
      Add New
    </Button>
  </Box>
);

const MyStackSection = ({ isSubmitting, onAddRow }: { isSubmitting: boolean; onAddRow: () => void }) => {
  const classes = useStyles();
  const { values, setFieldValue } = useFormikContext<MyFormValues>();
  const containers = values.myStackContainers;

  const replicateLast = () => {
    if (containers.length > 0) {
      setFieldValue('myStackContainers', [...containers, cloneDeep(containers[containers.length - 1])]);
    }
  };
  const removeRow = (index: number) =>
    setFieldValue(
      'myStackContainers',
      containers.filter((c, i) => i !== index)
    );

  return (
    <Box mb={4}>
      <Box pl={2} mb={1}>
        <Typography variant="h5" color="primary">
          My Stack
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Containers you will be reusing yourself — sent to the shipping line for reuse approval
        </Typography>
      </Box>
      {containers.length > 0 && (
        <TableContainer sx={{ paddingBottom: 1 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeader}></TableCell>
                <TableHeader sx={{ minWidth: 145 }} className={classes.tableHeader} label="SHIPPING LINE" />
                <TableHeader sx={{ minWidth: 145 }} className={classes.tableHeader} label="CONTAINER TYPE" />
                <TableHeader sx={{ minWidth: 145 }} className={classes.tableHeader} label="CONTAINER #" />
                <TableHeader
                  sx={{ minWidth: 160 }}
                  className={classes.tableHeader}
                  label="SHIPPING LINE EXPORT BOOKING"
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {containers.map((container, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Fab color="primary" size="small" onClick={() => removeRow(index)}>
                      <CloseIcon />
                    </Fab>
                  </TableCell>
                  <TableCell>
                    <ShippingLineSelectAsync name={`myStackContainers.${index}.shippingLineCode`} variant="standard" />
                  </TableCell>
                  <TableCell>
                    <ContainerTypeSelectAsync
                      name={`myStackContainers.${index}.containerTypeCode`}
                      variant="standard"
                    />
                  </TableCell>
                  <TableCell>
                    <ThemedTextField
                      name={`myStackContainers.${index}.containerCode`}
                      variant="standard"
                      placeholder="Code"
                    />
                  </TableCell>
                  <TableCell>
                    <ThemedTextField
                      name={`myStackContainers.${index}.shippingLineBookingRef`}
                      variant="standard"
                      placeholder="Export booking"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <SectionActions
        isSubmitting={isSubmitting}
        onAddRow={onAddRow}
        onReplicate={replicateLast}
        hasRows={containers.length > 0}
      />
    </Box>
  );
};

const VirtualStackSection = ({ isSubmitting, onAddRow }: { isSubmitting: boolean; onAddRow: () => void }) => {
  const classes = useStyles();
  const { values, setFieldValue } = useFormikContext<MyFormValues>();
  const containers = values.virtualStackContainers;

  const replicateLast = () => {
    if (containers.length > 0) {
      // must use cloneDeep so that file object is cloned properly
      setFieldValue('virtualStackContainers', [...containers, cloneDeep(containers[containers.length - 1])]);
    }
  };
  const removeRow = (index: number) =>
    setFieldValue(
      'virtualStackContainers',
      containers.filter((c, i) => i !== index)
    );

  return (
    <Box mb={4}>
      <Box pl={2} mb={1}>
        <Typography variant="h5" color="primary">
          Virtual Stack
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Containers available for anyone to reuse
        </Typography>
      </Box>
      {containers.length > 0 && (
        <TableContainer sx={{ paddingBottom: 1 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeader}></TableCell>
                <TableHeader sx={{ minWidth: 145 }} className={classes.tableHeader} label="SHIPPING LINE" />
                <TableHeader sx={{ minWidth: 145 }} className={classes.tableHeader} label="CONTAINER TYPE" />
                <TableHeader sx={{ minWidth: 145 }} className={classes.tableHeader} label="CONTAINER #" />
                <TableHeader sx={{ minWidth: 145 }} className={classes.tableHeader} label="DATE AVAILABLE FROM" />
                <TableHeader sx={{ minWidth: 145 }} className={classes.tableHeader} label="DATE AVAILABLE TO" />
                <TableHeader className={classes.tableHeader} label="AVAILABLE FROM" />
                <TableHeader className={classes.tableHeader} label="CLIENT / LOCATION FROM" />
                <TableHeader className={classes.tableHeader} label="CLEAN & ACCESSIBLE" center={true} />
                <TableHeader className={classes.tableHeader} label="UPLOAD PHOTO" center={true} />
              </TableRow>
            </TableHead>
            <TableBody>
              {containers.map((container, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Fab color="primary" size="small" onClick={() => removeRow(index)}>
                      <CloseIcon />
                    </Fab>
                  </TableCell>
                  <TableCell>
                    <ShippingLineSelectAsync
                      name={`virtualStackContainers.${index}.shippingLineCode`}
                      variant="standard"
                    />
                  </TableCell>
                  <TableCell>
                    <ContainerTypeSelectAsync
                      name={`virtualStackContainers.${index}.containerTypeCode`}
                      variant="standard"
                    />
                  </TableCell>
                  <TableCell>
                    <ThemedTextField
                      name={`virtualStackContainers.${index}.containerCode`}
                      variant="standard"
                      placeholder="Code"
                    />
                  </TableCell>
                  <TableCell>
                    <ThemedDatePicker name={`virtualStackContainers.${index}.availableFrom`} />
                  </TableCell>
                  <TableCell>
                    <ThemedDatePicker name={`virtualStackContainers.${index}.availableTo`} />
                  </TableCell>
                  <TableCell>
                    <ThemedTimePicker name={`virtualStackContainers.${index}.availableTimeFrom`} />
                  </TableCell>
                  <TableCell>
                    <ClientSelectAsync name={`virtualStackContainers.${index}.client_id`} />
                  </TableCell>
                  <TableCell align="center">
                    <ThemedRadioCheck name={`virtualStackContainers.${index}.cleanAndAcessible`} />
                  </TableCell>
                  <TableCell align="center">
                    <ImageUpload name={`virtualStackContainers.${index}.photos`} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <SectionActions
        isSubmitting={isSubmitting}
        onAddRow={onAddRow}
        onReplicate={replicateLast}
        hasRows={containers.length > 0}
      />
    </Box>
  );
};

const TableHeader = ({
  label,
  center = false,
  ...otherProps
}: { label: string; center?: boolean } & TableCellProps) => {
  return (
    <TableCell sx={{ minWidth: 50 }} {...otherProps}>
      <Typography
        noWrap={!center}
        variant="overline"
        lineHeight={1.5}
        color="textSecondary"
        align={center ? 'center' : 'inherit'}
        display="block"
      >
        {label}
      </Typography>
    </TableCell>
  );
};

export default AddContainerDrawer;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    close: {
      position: 'absolute',
      top: 0,
      right: 0,
    },
    margins: {
      marginTop: theme.spacing(16),
      marginBottom: theme.spacing(3),
      marginRight: theme.spacing(3),
      marginLeft: theme.spacing(14),
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      height: `calc(100% - ${theme.spacing(19)})`,
    },
    tableHeader: {
      backgroundColor: theme.palette.grey[100],
    },
    drawerPaper: {
      backgroundColor: theme.palette.grey[100],
      width: 'calc(100% - 196px)',
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
    },
  })
);
